import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, FormControl, InputLabel, Select, Stack } from '@pulse/ui';
import { useRef, useState, type ReactNode } from 'react';

export const ICON_SIZE = {
  /** Field/value captions (a `Field`-style value's caption, a `Stat`'s caption, a mobile-card row caption) — sits beside real content rather than a section title. */
  xxxs: 14,
  /** Table column headers, dialog table headers, and other dense/compact inline labels (e.g. a `Select` label in a tight toolbar `FormControl`). */
  xxs: 16,
  /** Icons sized to sit next to a line of body copy (e.g. a note's icon baseline-aligned to its own text) rather than a caption. */
  xs: 18,
  /** Section headers (a `Typography` subtitle) and standalone form field inline labels (a `TextField`/`Select` `label`). Matches MUI's built-in `fontSize="small"`. */
  sm: 'small',
  /** MUI's default icon size (`fontSize="medium"`, 24px) — a standalone action icon at its natural, unstyled size. */
  md: 'medium',
  /** MUI's built-in `fontSize="large"` (35px) — a prominent standalone icon. */
  lg: 'large',
  /** Large empty-state/placeholder icons shown above a "nothing here" caption. */
  xl: 40,
} as const;

export interface LabelWithIconProps {
  icon?: ReactNode;
  label: string;
  /** Right-align the icon+label — e.g. a right-aligned "Actions" table column header. */
  justifyContent?: 'flex-start' | 'flex-end';
  /**
   * Use when this renders inside a form field's label (a TextField `label` prop, a Select
   * `label` prop, or `InputLabel` children) rather than a table header/caption. Renders as an
   * inline `<span>` (valid inside the `<label>` element these sit in) in `display: inline-flex`,
   * so MUI's required-asterisk — appended as a plain sibling node after the label's children —
   * stays on the same line instead of wrapping onto its own line below a block-level flex box.
   */
  inline?: boolean;
}

// Shared leading-icon + label rendering for table headers, mobile-card field captions, and form
// field labels — one place so every page picks up the same icon size/color/spacing instead of
// each feature re-deriving its own. See apps/web/CLAUDE.md's "Icon-labeled fields" convention
// for which of LabelWithIcon/SelectWithIcon/IconRow to reach for, and which icon pairs with
// which entity/field.
export function LabelWithIcon({ icon, label, justifyContent = 'flex-start', inline = false }: LabelWithIconProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={justifyContent}
      spacing={0.5}
      component={inline ? 'span' : 'div'}
      sx={inline ? { display: 'inline-flex', verticalAlign: 'middle' } : undefined}
    >
      {icon}
      <span>{label}</span>
    </Stack>
  );
}

export interface IconRowProps {
  icon: ReactNode;
  children: ReactNode;
  /**
   * Vertical alignment of the icon against `children`. `'center'` (default) suits a single line
   * of text or a compact widget (e.g. a progress bar) — it keeps the icon centered against
   * whatever height the content actually renders at. `'flex-start'` suits content that can wrap
   * to multiple lines (e.g. a long name/title) — pins the icon to the *top* of the first line
   * instead of drifting to the vertical center of the whole wrapped block, but only lines up
   * exactly with the first line's glyphs when that line's own font-size/line-height are known —
   * a mismatch (e.g. unstyled HTML from a markdown renderer) leaves a few px of visible gap.
   * `'baseline'` also pins to the first line, but aligns the icon to that line's actual text
   * baseline rather than its box's top edge, so it lines up correctly regardless of the
   * content's font metrics — prefer this over `'flex-start'` when `children` isn't a `Typography`
   * with a known line-height (e.g. raw/markdown-rendered HTML).
   */
  align?: 'flex-start' | 'center' | 'baseline';
  spacing?: number;
}

// Leading icon next to an arbitrary value/title — not a field label/caption (see
// `LabelWithIcon` for that). Reach for this on a mobile card's primary field (a Name, a Key) or
// any other spot where the icon sits beside real content rather than a caption above it.
export function IconRow({ icon, children, align = 'center', spacing = 0.5 }: IconRowProps) {
  return (
    <Stack direction="row" alignItems={align} spacing={spacing}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

export interface SelectWithIconProps {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Forwarded to the underlying `FormControl` — e.g. `{ minWidth: 170 }` when `fullWidth={false}`
   *  would otherwise shrink to the narrowest selected option's content width. */
  sx?: SxProps<Theme>;
}

// Single-choice `<Select>` with a leading-icon label — collapses the FormControl/InputLabel/
// Select boilerplate that otherwise repeats the same `LabelWithIcon` twice per field (once for
// the floating `InputLabel`, once for `Select`'s own `label` prop, which MUI needs to size the
// outline notch). Reach for this any time a dropdown field needs the same icon+label treatment
// as a `TextField` built with `LabelWithIcon` — pass `<MenuItem>`s as children exactly as you
// would to a bare `Select`.
export function SelectWithIcon({ icon, label, value, onChange, children, required = false, fullWidth = true, disabled = false, sx }: SelectWithIconProps) {
  const renderedLabel = <LabelWithIcon icon={icon} label={label} inline />;
  // MUI's Select only ever sets the open menu's *minimum* width to match the field — long,
  // unwrapped option text still grows the popup wider than the field instead of wrapping.
  // Measuring the field's actual rendered width on open and feeding it back as a hard `width`
  // (not `minWidth`) keeps the menu the same width as the field for every SelectWithIcon
  // instance, with wrapping enabled on each option so long labels break onto multiple lines
  // instead of stretching the popup.
  const fieldRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number>();
  return (
    <FormControl ref={fieldRef} fullWidth={fullWidth} required={required} disabled={disabled} sx={sx}>
      <InputLabel required={required}>{renderedLabel}</InputLabel>
      <Select
        value={value}
        label={renderedLabel}
        disabled={disabled}
        onChange={(e: SelectChangeEvent<string>) => onChange(e.target.value)}
        onOpen={() => setMenuWidth(fieldRef.current?.offsetWidth)}
        MenuProps={{
          PaperProps: {
            style: { width: menuWidth },
            // maxHeight caps the popup so a field with many options (e.g. Business Unit) scrolls
            // internally instead of growing tall enough to cover most of the page — same 380px
            // ceiling FilterPill's own Menu already uses, for consistent dropdown sizing app-wide.
            sx: { maxHeight: 380, '& .MuiMenuItem-root': { whiteSpace: 'normal', wordBreak: 'break-word' } },
          },
        }}
      >
        {children}
      </Select>
    </FormControl>
  );
}
